/**
 * Date-of-birth validation.
 *
 * The terms already state that buyers must be 18 or over (Eligibility). This
 * is the half that enforces it wherever a date of birth is actually typed —
 * the customer's own settings page and the staff profile editor — so the
 * claim in the terms and the data in the database agree.
 *
 * Deliberately dependency-free so both worker routes and the test suite can
 * import it without pulling in the rest of the worker.
 *
 * Nothing here looks at existing rows. This validates input; it does not go
 * back through accounts that predate the check, which would be a separate
 * and much more consequential decision.
 */

export const MIN_AGE_YEARS = 18

// Below this the date is a typo or a joke, not a birth date, and letting it
// through would quietly break the birthday reward sweep's year arithmetic.
const EARLIEST_YEAR = 1900

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Parses YYYY-MM-DD strictly. The regex alone accepts 2026-02-30 and
 * 2025-02-29, so the parts are compared back against a real Date.
 */
export function parseIsoDate(value) {
  const m = ISO_DATE.exec(String(value ?? ''))
  if (!m) return null
  const [, y, mo, d] = m
  const year = Number(y), month = Number(mo), day = Number(d)
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  // UTC so the result never shifts with the server's timezone.
  const dt = new Date(Date.UTC(year, month - 1, day))
  if (dt.getUTCFullYear() !== year || dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day) return null
  return { year, month, day }
}

/**
 * Completed years between a birth date and a reference date.
 *
 * Compared as (month, day) tuples rather than by subtracting timestamps:
 * that avoids every leap-year and DST trap, and gives the conventional
 * answer for a 29 February birth date — they turn 18 on 1 March in a
 * non-leap year, not on 28 February.
 */
export function ageOn(birth, today = new Date()) {
  const ty = today.getUTCFullYear()
  const tm = today.getUTCMonth() + 1
  const td = today.getUTCDate()
  let age = ty - birth.year
  if (tm < birth.month || (tm === birth.month && td < birth.day)) age -= 1
  return age
}

/**
 * The single answer to "may this date of birth be saved?".
 *
 * Returns { ok: true, value } for an acceptable date — including the empty
 * string, which means "clear it", since a birthday is optional and only
 * feeds the birthday reward.
 *
 * The message is deliberately brand-free: the worker serves whatever store
 * is deployed on it. The storefront brands it with the configured company
 * name before showing it.
 */
export function validateDateOfBirth(raw, { today = new Date(), minAge = MIN_AGE_YEARS } = {}) {
  const value = String(raw ?? '').trim()
  if (!value) return { ok: true, value: null }

  const birth = parseIsoDate(value)
  if (!birth) return { ok: false, error: 'Enter a real date of birth as YYYY-MM-DD.' }

  if (birth.year < EARLIEST_YEAR) {
    return { ok: false, error: 'Enter a real date of birth as YYYY-MM-DD.' }
  }

  const age = ageOn(birth, today)
  if (age < 0) return { ok: false, error: 'A date of birth cannot be in the future.' }
  if (age < minAge) {
    return {
      ok: false,
      age,
      error: `You must be at least ${minAge} years old to use this store.`,
    }
  }

  return { ok: true, value, age }
}

/**
 * The latest date of birth that is old enough, as YYYY-MM-DD.
 *
 * Feeds the `max` attribute on the date inputs, so the browser's own picker
 * will not offer an under-age date in the first place — a better experience
 * than typing one and being told no.
 */
export function latestAllowedDob(today = new Date(), minAge = MIN_AGE_YEARS) {
  const d = new Date(Date.UTC(
    today.getUTCFullYear() - minAge,
    today.getUTCMonth(),
    today.getUTCDate(),
  ))
  return d.toISOString().slice(0, 10)
}
